import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';
import { ChevronDown, Search, MessageCircle, Ruler, ShoppingBag, Truck, CreditCard, RotateCcw, Palette, Store, Bookmark, Tag } from 'lucide-react';

const CATEGORIES = [
  { key: 'all', label: 'Todo', icon: null },
  { key: 'pedidos', label: 'Pedidos', icon: ShoppingBag },
  { key: 'pagos', label: 'Pagos', icon: CreditCard },
  { key: 'envios', label: 'Envíos', icon: Truck },
  { key: 'productos', label: 'Productos', icon: Palette },
];

const faqs = [
  {
    category: 'pedidos',
    question: '¿Cómo realizo mi pedido?',
    answer: 'Navega por nuestra tienda, selecciona los productos que deseas, elige tu talla y color, y agrégalos al carrito. Una vez listo, haz clic en "Finalizar Pedido" y serás redirigido a WhatsApp donde podrás confirmar tu orden directamente con nosotros.'
  },
  {
    category: 'pagos',
    question: '¿Cuáles son los métodos de pago?',
    answer: 'Aceptamos: Transferencias bancarias (Banesco, Mercantil, Provincial), Pago móvil, Zelle (para pagos en dólares), y efectivo contra entrega en zonas selectas. Te confirmaremos los datos de pago una vez realices tu pedido por WhatsApp.'
  },
  {
    category: 'envios',
    question: '¿Hacen envíos a toda Venezuela?',
    answer: '¡Sí! Realizamos envíos a todo el territorio nacional a través de MRW, Zoom, Tealca y Domesa. El costo del envío depende de tu ubicación y será calculado al momento de confirmar tu pedido.'
  },
  {
    category: 'envios',
    question: '¿Cuánto tarda en llegar mi pedido?',
    answer: 'Una vez confirmado el pago, preparamos tu pedido en 24-48 horas. Caracas y zonas metropolitanas: 2-3 días hábiles. Interior del país: 3-5 días hábiles. Te proporcionaremos número de seguimiento.'
  },
  {
    category: 'pedidos',
    question: '¿Puedo cambiar o devolver mi producto?',
    answer: 'Aceptamos cambios por talla dentro de los primeros 7 días después de recibir tu pedido, siempre que el producto esté sin usar, con etiquetas y en su empaque original. Los costos de envío del cambio corren por cuenta del cliente.'
  },
  {
    category: 'productos',
    question: '¿Cómo elijo mi talla correcta?',
    answer: 'Consulta nuestra Guía de Tallas donde encontrarás las medidas exactas. Mide tu busto, cintura y cadera y compáralas con nuestra tabla. Si tienes dudas, escríbenos por WhatsApp y te asesoramos personalmente.'
  },
  {
    category: 'productos',
    question: '¿Los colores son exactos a las fotos?',
    answer: 'Hacemos todo lo posible para que las fotos reflejen fielmente los colores. Sin embargo, pueden variar ligeramente según tu pantalla. Si necesitas más fotos o videos, ¡te los enviamos por WhatsApp!'
  },
  {
    category: 'productos',
    question: '¿Tienen tienda física?',
    answer: 'Actualmente operamos exclusivamente online. Ocasionalmente participamos en ferias y eventos de moda. ¡Síguenos en Instagram para enterarte de los próximos!'
  },
  {
    category: 'pedidos',
    question: '¿Puedo apartar un producto?',
    answer: 'Sí, con un abono del 50%. Plazo máximo para completar el pago: 7 días. Pasado este tiempo, el abono se convierte en crédito para futuras compras.'
  },
  {
    category: 'pagos',
    question: '¿Ofrecen descuentos o promociones?',
    answer: 'Sí, regularmente ofrecemos descuentos especiales y ofertas exclusivas. Síguenos en Instagram (@davocadoswimwear) y únete a nuestra lista de WhatsApp para enterarte primero.'
  }
];

const FAQItem = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      className="border-b border-white/[0.04] last:border-0"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className={`text-[15px] font-medium pr-8 transition-colors duration-200 ${isOpen ? 'text-gold' : 'text-white/80 group-hover:text-white'}`}>
          {faq.question}
        </span>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-gold/10 rotate-180' : 'bg-white/[0.04]'}`}>
          <ChevronDown className={`w-4 h-4 transition-colors ${isOpen ? 'text-gold' : 'text-white/30'}`} />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <p className="text-white/40 text-sm leading-relaxed pb-5 pr-12">
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { trackPageView('/faq', 'Preguntas Frecuentes'); }, []);

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = !searchQuery || faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="faq-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-8 pb-6"
        >
          <h1 className="text-2xl md:text-3xl font-serif text-white mb-2">Preguntas frecuentes</h1>
          <p className="text-white/30 text-sm">Respuestas rápidas a lo que más nos preguntan</p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative mb-6"
        >
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar en preguntas..."
            className="w-full h-11 pl-10 pr-4 bg-white/[0.03] rounded-xl text-white text-sm placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-white/10 transition-all"
          />
        </motion.div>

        {/* Category tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-6 -mx-4 px-4"
        >
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                  activeCategory === cat.key
                    ? 'bg-white text-black'
                    : 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/60'
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* FAQ List */}
        <div className="bg-white/[0.02] rounded-2xl px-5">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <FAQItem key={faq.question} faq={faq} index={index} />
            ))
          ) : (
            <div className="py-16 text-center">
              <Search className="w-8 h-8 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm mb-1">Sin resultados</p>
              <button onClick={() => { setSearchQuery(''); setActiveCategory('all'); }} className="text-gold text-xs">
                Ver todas las preguntas
              </button>
            </div>
          )}
        </div>

        {/* Result count */}
        {searchQuery && filteredFaqs.length > 0 && (
          <p className="text-white/15 text-xs text-center mt-3">
            {filteredFaqs.length} resultado{filteredFaqs.length !== 1 ? 's' : ''}
          </p>
        )}

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 mb-16"
        >
          <div className="bg-white/[0.02] rounded-2xl p-6 md:p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-5 h-5 text-[#25D366]" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">¿Tienes otra pregunta?</h2>
            <p className="text-white/30 text-sm mb-5 max-w-sm mx-auto">
              Nuestro equipo responde en menos de 1 hora
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
              <a
                href="https://wa.me/584242925792?text=Hola!%20Tengo%20una%20pregunta"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-[#25D366] hover:bg-[#20BA5C] text-white rounded-full text-sm font-medium transition-colors"
              >
                Escribir por WhatsApp
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center h-11 px-6 bg-white/[0.04] hover:bg-white/[0.08] text-white/60 rounded-full text-sm transition-colors"
              >
                Ir a contacto
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Quick links */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 pb-16">
          <Link to="/guia-de-tallas" className="flex items-center gap-1.5 text-white/20 hover:text-gold text-xs transition-colors">
            <Ruler className="w-3 h-3" />
            Guía de tallas
          </Link>
          <Link to="/tienda" className="flex items-center gap-1.5 text-white/20 hover:text-gold text-xs transition-colors">
            <ShoppingBag className="w-3 h-3" />
            Ir a la tienda
          </Link>
          <Link to="/nosotros" className="flex items-center gap-1.5 text-white/20 hover:text-gold text-xs transition-colors">
            <Store className="w-3 h-3" />
            Sobre nosotros
          </Link>
        </div>
      </div>
    </div>
  );
}
